import { IconProps } from '@amzn/awsui-components-console/icon';
import iconTeamsNoCms from 'src/images/icon-teams-no-cms.svg';

/** URLs */
export const PILOT_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=ShCAUi1HOEWczx4dDv6LGy-0rtE_qJtNhDYu3fd0hVZUMDlaNTI1S0ZQQkJERDdaNlhIMlBJR0VQNSQlQCN0PWcu&route=shorturl';
export const SURVEY_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=ShCAUi1HOEWczx4dDv6LGyzXzV01jN5GkW6ceQj7iqJUMUE3WDNNNjJXUFJaTjdDNEJPMkRJWFhNTy4u&route=shorturl';

export interface FeatureCard {
  iconName: IconProps.Name;
  title: string;
  description: string;
  badge?: string;
}

export interface UserTypeCard {
  iconName?: IconProps.Name;
  iconSvg?: string;
  title: string;
  description: string;
}

export const features: FeatureCard[] = [
  {
    iconName: 'edit',
    title: 'Create your strings',
    description:
      'Create and edit source strings with support for plain text, HTML, markdown, and message format syntaxes.',
  },
  {
    iconName: 'globe',
    title: 'Hands-free translation management',
    description:
      'We manage your translations and ensure you get the desired quality within your SLA — submit and forget it.',
  },
  {
    iconName: 'key',
    title: 'Granular and easy access management',
    description:
      'Manage your access through Bindles — easy to scale, with a full audit trail.',
  },
  {
    iconName: 'refresh',
    title: 'Track your translations',
    description: 'Track every active translation request in one place.',
  },
  {
    iconName: 'folder',
    title: 'Market-specific source management',
    description:
      'Create market-specific variants for the same string ID — flexibility for Amazon scale.',
  },
  {
    iconName: 'gen-ai',
    title: 'LLM-powered translations',
    description: 'Translate with custom LLM models built for Amazon content.',
  },
  {
    iconName: 'star-filled',
    title: 'Amazon grade quality',
    description:
      'Professional linguists and quality teams review your translations where it matters.',
  },
  {
    iconName: 'check',
    title: 'Review your strings',
    description:
      'Built-in feature for teams to ensure all strings are checked before submission.',
    badge: 'Coming Soon',
  },
  {
    iconName: 'file',
    title: 'Edit your translation',
    description:
      'Directly edit your translations on LangHub, no external support needed.',
    badge: 'Coming Soon',
  },
  {
    iconName: 'upload-download',
    title: 'Built for Amazon scale',
    description: 'Create, review, and edit in bulk with a single interaction.',
    badge: 'Coming Soon',
  },
  {
    iconName: 'settings',
    title: 'String testing',
    description:
      'Validate translations render correctly before you push to production.',
    badge: 'Coming Soon',
  },
];

export const userTypes: UserTypeCard[] = [
  {
    iconSvg: iconTeamsNoCms,
    title: 'Teams without a CMS',
    description:
      "If you don't have your own UI or CMS for authoring strings, LangHub is your full-featured platform for the entire string lifecycle.",
  },
  {
    iconName: 'upload-download',
    title: 'Current AST users',
    description:
      'LangHub replaces AST with modern guardrails, guaranteed coverage, and the latest translation models to improve your workflow.',
  },
  {
    iconName: 'globe',
    title: 'Panther users',
    description:
      'Use LangHub for source review, translation review, and translation overwrites to maintain quality across your locales.',
  },
  {
    iconName: 'history',
    title: 'Current LUI users',
    description:
      'Use LangHub for translation overwrites, with full audit trail and quality capture.',
  },
];

/** Roadmap */
export interface RoadmapItem {
  milestone: string;
  capabilities: string;
  target: string;
}

export const ROADMAP_ITEMS: RoadmapItem[] = [
  {
    milestone: 'MVP',
    capabilities:
      'Create & edit source strings, variants, submit & cancel your job, search, and track your translation',
    target: 'Oct 2026',
  },
  {
    milestone: 'M2',
    capabilities: 'Translation overwrite, bulk actions, and drafts',
    target: 'Q1 2027',
  },
  {
    milestone: 'M3',
    capabilities: 'Source review & staging',
    target: 'H1 2027',
  },
  {
    milestone: 'M4',
    capabilities:
      'Data migration & other migration tooling needed to move from AST',
    target: 'Q3 2027',
  },
  {
    milestone: 'Migration',
    capabilities: 'Begin migration for users',
    target: 'Q4 2027',
  },
];

/** AST vs LangHub comparison */
export interface ComparisonItem {
  capability: string;
  ast: string;
  langhub: string;
  comingSoon: boolean;
}

export const COMPARISON_ITEMS: ComparisonItem[] = [
  {
    capability: 'Custom LLM translation model',
    ast: 'Not available',
    langhub: 'Available — custom LLM models built for Amazon content',
    comingSoon: false,
  },
  {
    capability: 'Translation coverage',
    ast: 'Manual selection of locales for each string',
    langhub: 'CX ID based coverage, no manual selection needed',
    comingSoon: false,
  },
  {
    capability: 'Variants',
    ast: 'Available',
    langhub: 'Available',
    comingSoon: false,
  },
  {
    capability: 'Source review',
    ast: 'Not available',
    langhub: 'Built-in source review made available',
    comingSoon: true,
  },
  {
    capability: 'Translation overwrite',
    ast: 'Available',
    langhub: 'Available with a full audit trail',
    comingSoon: true,
  },
  {
    capability: 'Bulk actions',
    ast: 'Available',
    langhub: 'Bulk create, edit, overwrite, and source review available',
    comingSoon: true,
  },
  {
    capability: 'Cancel',
    ast: 'Available per string',
    langhub: 'Available at string group level',
    comingSoon: false,
  },
  {
    capability: 'Search',
    ast: 'Available',
    langhub: 'Faster and more accurate',
    comingSoon: false,
  },
  {
    capability: 'Tracking your jobs',
    ast: 'Checked string by string',
    langhub: 'One place for all your live and historical jobs',
    comingSoon: false,
  },
  {
    capability: 'Notifications',
    ast: 'Limited',
    langhub: 'Built-in email notifications available',
    comingSoon: false,
  },
  {
    capability: 'Access management',
    ast: 'Managed through POSIX groups',
    langhub:
      'Managed through Bindles — granular, namespace-level, with a full audit trail',
    comingSoon: false,
  },
  {
    capability: 'Unit of work',
    ast: 'The individual string',
    langhub:
      'The string group — submit, cancel, and track a whole group at once',
    comingSoon: false,
  },
  {
    capability: 'Staging',
    ast: 'Alpha, Beta, Gamma, and Prod',
    langhub: 'One Prod and one Non-Prod',
    comingSoon: true,
  },
  {
    capability: 'AI chatbot',
    ast: 'Not available',
    langhub: 'Available',
    comingSoon: true,
  },
];
